import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import MessageMarkdownContent from "_shared/_views/markdown/message-markdown-content";
import MessageHistoryControl from "messaging/_views/components/message-history-dialog";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import BubbleTimestamp from "messaging/correspondence/_views/bubble-timestamp";
import EditedMark from "messaging/correspondence/_views/edited-mark";
import { MessageActionButton } from "messaging/correspondence/_views/message-actions";
import MessageEditField from "messaging/correspondence/_views/message-edit-field";
import {
  BubbleSubjectChip,
  BubbleWithheldText,
  MessageBubble,
  MessageBubbleFiles,
  NO_BODY_TEXT,
} from "messaging/correspondence/_views/message-bubble";
import Message from "messaging/message";
import React from "react";

type SentMessageProps = {
  message: Message;
  onImageLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
  onImageClick: (message: Message, image: MessageFileInfo) => void;
  onDownload: (message: Message, attachment: MessageFileInfo) => void;
  /** Offers Copy to documents on each of the message's files (#6053). */
  onCopyToDocuments?: (message: Message, attachment: MessageFileInfo) => void;
  /**
   * Offers Edit. Called with the rewritten text; answers whether the edit was saved, and the box stays
   * open on a refusal so the author keeps what they wrote.
   */
  onEdit?: (message: Message, text: string) => Promise<boolean>;
  /** Offers Delete. Answers whether the message was deleted. */
  onDelete?: (message: Message) => Promise<boolean>;
};

/** A message the reader sent, in the dark bubble on the right. */
export const SentMessage = React.forwardRef<HTMLDivElement, SentMessageProps & React.HTMLAttributes<HTMLDivElement>>(
  function SentMessage(props, ref) {
    const {
      message,
      onImageLoad,
      onImageClick,
      onDownload,
      onCopyToDocuments,
      onEdit,
      onDelete,
      ...otherProps
    } = props;
    const [editText, setEditText] = React.useState<string>();
    const [isSavingEdit, setIsSavingEdit] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const isEditing = editText !== undefined;
    const isLive = !message.isDeleted && !message.retracted;

    async function saveEdit(text: string) {
      if (!onEdit || text.trim() === "") return;
      setIsSavingEdit(true);
      const saved = await onEdit(message, text);
      setIsSavingEdit(false);
      if (saved) setEditText(undefined);
    }

    async function remove() {
      if (!onDelete) return;
      setIsDeleting(true);
      const deleted = await onDelete(message);
      if (!deleted) setIsDeleting(false);
    }

    const historyLabel = message.isDeleted ? "Deleted" : message.retracted ? "Removed" : "Edited";

    return (
      <MessageBubble
        ref={ref}
        side="sent"
        time={message.published && <BubbleTimestamp date={message.published} />}
        status={
          <>
            <EditedMark message={message} />
            {(message.isDeleted || message.retracted || message.updated) && (
              <MessageHistoryControl message={message} label={historyLabel} />
            )}
          </>
        }
        controls={
          isLive &&
          !isEditing && (
            <>
              {onEdit && (
                <MessageActionButton
                  label="Edit message"
                  icon={<EditIcon fontSize="inherit" />}
                  onClick={() => setEditText(message.content ?? "")}
                />
              )}
              {onDelete && (
                <MessageActionButton
                  label="Delete message"
                  icon={<DeleteIcon fontSize="inherit" />}
                  destructive
                  disabled={isDeleting}
                  onClick={() => void remove()}
                />
              )}
            </>
          )
        }
        {...otherProps}
      >
        <div>
          {!message.isDeleted && message.subject && message.subject.length > 0 && (
            <BubbleSubjectChip label={`RE: ${message.subject}`} size="small" />
          )}
        </div>
        {message.isDeleted && <BubbleWithheldText>This message was deleted</BubbleWithheldText>}
        {!message.isDeleted && isEditing && (
          <MessageEditField
            text={editText}
            saving={isSavingEdit}
            saveLabel="Save message"
            onChange={setEditText}
            onSave={() => void saveEdit(editText)}
            onCancel={() => setEditText(undefined)}
          />
        )}
        {!message.isDeleted && !isEditing && message.hasNoBody && <BubbleWithheldText>{NO_BODY_TEXT}</BubbleWithheldText>}
        {!message.isDeleted && !isEditing && !message.hasNoBody && <MessageMarkdownContent content={message.content ?? ""} />}
        {!message.isDeleted && (
          <MessageBubbleFiles
            side="sent"
            files={message.attachments ?? []}
            forumId={message.forum.id?.value}
            messageId={message.id?.value}
            onImageLoad={onImageLoad}
            onImageClick={(image) => onImageClick(message, image)}
            onDownload={(file) => onDownload(message, file)}
            onCopyToDocuments={onCopyToDocuments ? (file) => onCopyToDocuments(message, file) : undefined}
          />
        )}
      </MessageBubble>
    );
  },
);
